import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useBabyProfiles } from '../../contexts/BabyProfileContext';
import { babyProfileService } from '../../services/babyProfileService';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateToLong } from '../../utils/dateUtils';

// Styled Components
const PageContainer = styled.div`
  padding: 2rem 0;
`;

const BackLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  
  &:hover {
    text-decoration: underline;
  }
  
  i {
    margin-right: 0.5rem;
  }
`;

const ProfileCard = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const ProfileHeader = styled.div`
  background-color: var(--primary);
  padding: 2rem;
  color: white;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(to bottom, rgba(0,0,0,0.1), transparent);
  }
`;

const ProfileTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  h1 {
    margin: 0;
    font-size: 2rem;
  }
`;

const ProfileMeta = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  
  span {
    display: flex;
    align-items: center;
    margin-right: 1.5rem;
    
    i {
      margin-right: 0.5rem;
    }
  }
`;

const ProfileContent = styled.div`
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  color: var(--primary);
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const InfoCard = styled.div`
  background-color: ${props => props.bgColor || '#f8f9fa'};
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid ${props => props.borderColor || 'var(--primary)'};
`;

const InfoItem = styled.div`
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .label {
    font-size: 0.9rem;
    color: var(--gray);
    margin-bottom: 0.3rem;
  }
  
  .value {
    font-weight: 500;
    color: var(--dark);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  i {
    margin-right: 0.5rem;
  }
  
  &.primary {
    background-color: var(--primary);
    color: white;
    border: none;
    
    &:hover {
      background-color: var(--primary-dark);
      transform: translateY(-2px);
    }
  }
  
  &.secondary {
    background-color: white;
    color: var(--primary);
    border: 1px solid var(--primary);
    
    &:hover {
      background-color: #f8f9fa;
      transform: translateY(-2px);
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  
  .spinner-border {
    color: var(--primary);
    margin-bottom: 1rem;
  }
`;

const ErrorContainer = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const BabyProfileView = () => {
  const { profileId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { profiles, loading, error, refreshProfiles } = useBabyProfiles();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  
  // Check if profile was passed in location state
  useEffect(() => {
    if (location.state && location.state.profile) {
      console.log("Using profile from location state:", location.state.profile);
      setProfile(location.state.profile);
    }
  }, [location.state]);
  
  // Extract profileId from URL if params don't work
  const getProfileIdFromPath = () => {
    const pathParts = location.pathname.split('/');
    return pathParts[pathParts.length - 1];
  };
  
  const effectiveProfileId = profileId || getProfileIdFromPath();
  
  // Find profile in context if not in location state
  useEffect(() => {
    if (!profile && profiles && profiles.length > 0 && effectiveProfileId) {
      console.log("Looking for profile with ID:", effectiveProfileId);
      console.log("Available profiles:", JSON.stringify(profiles));
      
      // Try with exact match first
      let foundProfile = profiles.find(p => p.profileId === effectiveProfileId);
      
      // If not found, try with string comparison (in case of type mismatch)
      if (!foundProfile) {
        foundProfile = profiles.find(p => String(p.profileId) === String(effectiveProfileId));
      }
      
      if (foundProfile) {
        console.log("Found profile:", foundProfile);
        setProfile(foundProfile);
      } else {
        console.log(`Profile with ID ${effectiveProfileId} not found in profiles`);
      }
    }
  }, [profiles, effectiveProfileId, profile]);
  
  // Fetch profile directly from API if not found in context
  useEffect(() => {
    const fetchProfileDirectly = async () => {
      if (!profile && currentUser && effectiveProfileId) {
        try {
          console.log("Fetching profile directly from API for ID:", effectiveProfileId);
          const fetchedProfile = await babyProfileService.getBabyProfile(currentUser.idToken, effectiveProfileId);
          if (fetchedProfile) {
            console.log("Fetched profile directly:", fetchedProfile);
            setProfile(fetchedProfile);
          }
        } catch (err) {
          console.error("Error fetching profile directly:", err);
        }
      }
    };
    
    fetchProfileDirectly();
  }, [profile, currentUser, effectiveProfileId]);
  
  // For debugging - log when component mounts
  useEffect(() => {
    console.log("BabyProfileView component mounted");
    console.log("URL path:", location.pathname);
    console.log("Params profileId:", profileId);
    console.log("Effective profileId:", effectiveProfileId);
  }, [location.pathname, profileId, effectiveProfileId]);
  
  // Debug the profile structure
  useEffect(() => {
    if (profile) {
      console.log("Profile structure:", profile);
    }
  }, [profile]);
  
  // Helper function to safely access nested properties
  const getNestedValue = (obj, path, defaultValue = 'Not specified') => {
    try {
      const result = path.split('.').reduce((o, key) => (o && o[key] !== undefined) ? o[key] : undefined, obj);
      return result !== undefined ? result : defaultValue;
    } catch (error) {
      console.error(`Error accessing path ${path}:`, error);
      return defaultValue;
    }
  };
  
  const handleEditProfile = () => {
    navigate(`/edit-child/${profileId}`);
  };
  
  const handleViewSleepPlan = () => {
    navigate(`/view-sleep-plan/${profileId}`);
  };
  
  // Format sleep associations for display
  const formatSleepAssociations = (associations) => {
    if (!associations) return 'None specified';
    
    const items = [];
    if (associations.rocking || associations.rocked) items.push('Rocking');
    if (associations.feeding || associations.fedToSleep) items.push('Feeding');
    if (associations.dummy) items.push('Dummy/Pacifier');
    if (associations.patting) items.push('Patting');
    if (associations.holding) items.push('Holding');
    if (associations.carRide) items.push('Car Ride');
    if (associations.stroller) items.push('Stroller');
    if (associations.whiteNoise) items.push('White Noise');
    if (associations.swaddle) items.push('Swaddle');
    if (associations.independentlySleeps) items.push('Independently falls asleep');
    if (associations.other) items.push('Other');
    
    return items.length > 0 ? items.join(', ') : 'None specified';
  };
  
  // Format sleep challenges for display
  const formatSleepChallenges = (challenges) => {
    if (!challenges) return 'None specified';
    
    const items = [];
    if (challenges.frequentWaking) items.push('Frequent Waking');
    if (challenges.shortNaps) items.push('Short Naps');
    if (challenges.earlyWaking) items.push('Early Waking');
    if (challenges.difficultyFallingAsleep) items.push('Difficulty Falling Asleep');
    if (challenges.nightFeedings) items.push('Night Feedings');
    if (challenges.other) items.push('Other');
    
    return items.length > 0 ? items.join(', ') : 'None specified';
  };
  
  // Format main goals for display
  const formatMainGoals = (goals) => {
    if (!goals) return 'None specified';
    
    const items = [];
    if (goals.longerStretches) items.push('Longer stretches of sleep');
    if (goals.selfSettling) items.push('Self-settling');
    if (goals.betterNaps) items.push('Better naps');
    if (goals.reducingNightWakings) items.push('Reducing night wakings');
    
    return items.length > 0 ? items.join(', ') : 'None specified';
  };
  
  // Format response to wakes for display
  const formatResponseToWakes = (responseToWakes, responseToWakesOther) => {
    if (!responseToWakes) return 'None specified';
    
    const items = [];
    if (responseToWakes.feed) items.push('Feed');
    if (responseToWakes.rock) items.push('Rock');
    if (responseToWakes.dummy) items.push('Dummy/Pacifier');
    if (responseToWakes.cuddle) items.push('Cuddle');
    if (responseToWakes.wait) items.push('Wait');
    
    if (responseToWakes.other && responseToWakesOther) {
      items.push(`Other: ${responseToWakesOther}`);
    } else if (responseToWakes.other) {
      items.push('Other');
    }
    
    return items.length > 0 ? items.join(', ') : 'None specified';
  };
  
  if (loading) {
    return (
      <div className="container">
        <PageContainer>
          <LoadingContainer>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading baby profile...</p>
          </LoadingContainer>
        </PageContainer>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container">
        <PageContainer>
          <BackLink to="/dashboard">
            <i className="bi bi-arrow-left"></i> Back to Dashboard
          </BackLink>
          <ErrorContainer>
            <p>Error loading profile: {error.message}</p>
            <Button className="primary" onClick={refreshProfiles}>
              <i className="bi bi-arrow-clockwise"></i> Retry
            </Button>
          </ErrorContainer>
        </PageContainer>
      </div>
    );
  }
  
  if (!profile) {
    return (
      <div className="container">
        <PageContainer>
          <BackLink to="/dashboard">
            <i className="bi bi-arrow-left"></i> Back to Dashboard
          </BackLink>
          <ErrorContainer>
            <p>Profile not found. Please return to the dashboard and try again.</p>
            <Button className="primary" onClick={() => navigate('/dashboard')}>
              <i className="bi bi-house"></i> Go to Dashboard
            </Button>
          </ErrorContainer>
        </PageContainer>
      </div>
    );
  }
  
  return (
    <div className="container">
      <PageContainer>
        <BackLink to="/dashboard">
          <i className="bi bi-arrow-left"></i> Back to Dashboard
        </BackLink>
        
        <ProfileCard>
          <ProfileHeader>
            <ProfileTitle>
              <h1>{profile.babyName}'s Profile</h1>
            </ProfileTitle>
            <ProfileMeta>
              <span>
                <i className="bi bi-calendar"></i> 
                {babyProfileService.calculateAge(profile.dateOfBirth)}
              </span>
              <span>
                <i className="bi bi-gender-{profile.gender === 'Male' ? 'male' : 'female'}"></i> 
                
              </span>
            </ProfileMeta>
          </ProfileHeader>
          
          <ProfileContent>
            <SectionTitle>Basic Information</SectionTitle>
            <InfoGrid>
              <InfoCard borderColor="var(--primary)">
                <InfoItem>
                  <div className="label">Date of Birth</div>
                  <div className="value">{formatDateToLong(profile.dateOfBirth)}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Age</div>
                  <div className="value">{babyProfileService.calculateAge(profile.dateOfBirth)}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Gender</div>
                  <div className="value">{profile.gender}</div>
                </InfoItem>
              </InfoCard>

              {getNestedValue(profile, 'healthInfo.isPremature') === true && (
                <InfoCard borderColor="var(--primary)">
                  <InfoItem>
                    <div className="label">Premature</div>
                    <div className="value">Yes</div>
                  </InfoItem>
                  {getNestedValue(profile, 'healthInfo.correctedAge') && (
                    <InfoItem>
                      <div className="label">Corrected Age</div>
                      <div className="value">{getNestedValue(profile, 'healthInfo.correctedAge')}</div>
                    </InfoItem>
                  )}
                </InfoCard>
              )}
            
            </InfoGrid>
            
            <SectionTitle>Sleep Patterns</SectionTitle>
            <InfoGrid>
              <InfoCard borderColor="var(--secondary)" bgColor="rgba(126, 203, 185, 0.05)">
                <InfoItem>
                  <div className="label">Bedtime</div>
                  <div className="value">{getNestedValue(profile, 'sleepHabits.bedtime')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Wake-up Time</div>
                  <div className="value">{getNestedValue(profile, 'sleepHabits.wakeupTime')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Number of Naps</div>
                  <div className="value">{getNestedValue(profile, 'sleepHabits.numberOfNaps')}</div>
                </InfoItem>
              </InfoCard>
              
              <InfoCard borderColor="var(--secondary)" bgColor="rgba(126, 203, 185, 0.05)">
                <InfoItem>
                  <div className="label">Nap Duration</div>
                  <div className="value">{getNestedValue(profile, 'sleepHabits.napDurations')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Self-Settles</div>
                  <div className="value">{getNestedValue(profile, 'sleepHabits.selfSettles')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Sleep Associations</div>
                  <div className="value">{formatSleepAssociations(getNestedValue(profile, 'sleepHabits.sleepAssociations', {}))}</div>
                </InfoItem>
              </InfoCard>
            </InfoGrid>
            
            <SectionTitle>Feeding Information</SectionTitle>
            <InfoGrid>
              <InfoCard borderColor="var(--purple)" bgColor="rgba(139, 110, 180, 0.05)">
                <InfoItem>
                  <div className="label">Feeding Type</div>
                  <div className="value">{getNestedValue(profile, 'feedingInfo.feedingType')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Overnight Feeds</div>
                  <div className="value">{getNestedValue(profile, 'feedingInfo.overnightFeeds')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Falls Asleep During Feeds</div>
                  <div className="value">{getNestedValue(profile, 'feedingInfo.fallsAsleepDuringFeeds')}</div>
                </InfoItem>
              </InfoCard>
              
              <InfoCard borderColor="var(--purple)" bgColor="rgba(139, 110, 180, 0.05)">
                <InfoItem>
                  <div className="label">Wakes Multiple Times</div>
                  <div className="value">{getNestedValue(profile, 'feedingInfo.wakesMultipleTimes')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Response to Wakes</div>
                  <div className="value">
                    {formatResponseToWakes(
                      getNestedValue(profile, 'feedingInfo.responseToWakes', {}),
                      getNestedValue(profile, 'feedingInfo.responseToWakesOther')
                    )}
                  </div>
                </InfoItem>
              </InfoCard>
            </InfoGrid>
            
            <SectionTitle>Sleep Environment</SectionTitle>
            <InfoGrid>
              <InfoCard borderColor="var(--accent)" bgColor="rgba(255, 170, 138, 0.05)">
                <InfoItem>
                  <div className="label">Sleep Location</div>
                  <div className="value">{getNestedValue(profile, 'sleepEnvironment.sleepLocation')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Dark Room</div>
                  <div className="value">{getNestedValue(profile, 'sleepEnvironment.isDarkRoom')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">White Noise</div>
                  <div className="value">{getNestedValue(profile, 'sleepEnvironment.usesWhiteNoise')}</div>
                </InfoItem>
              </InfoCard>
              
              <InfoCard borderColor="var(--accent)" bgColor="rgba(255, 170, 138, 0.05)">
                <InfoItem>
                  <div className="label">Sleepwear</div>
                  <div className="value">{getNestedValue(profile, 'sleepEnvironment.sleepwear')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Room Temperature</div>
                  <div className="value">{getNestedValue(profile, 'sleepEnvironment.roomTemperature')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Room Sharing</div>
                  <div className="value">{getNestedValue(profile, 'sleepEnvironment.roomSharing')}</div>
                </InfoItem>
              </InfoCard>
            </InfoGrid>
            
            <SectionTitle>Sleep Challenges & Goals</SectionTitle>
            <InfoGrid>
              <InfoCard borderColor="var(--primary)" bgColor="rgba(233, 64, 87, 0.05)">
                
                <InfoItem>
                  <div className="label">Parenting Style Preference</div>
                  <div className="value">{getNestedValue(profile, 'healthInfo.parentingStylePreference')}</div>
                </InfoItem>
                <InfoItem>
                  <div className="label">Main Goals</div>
                  <div className="value">{formatMainGoals(getNestedValue(profile, 'healthInfo.mainGoals', {}))}</div>
                </InfoItem>
              </InfoCard>
              
              {getNestedValue(profile, 'healthInfo.extraNotes') && (
                <InfoCard borderColor="var(--primary)" bgColor="rgba(233, 64, 87, 0.05)">
                  <InfoItem>
                    <div className="label">Additional Notes</div>
                    <div className="value">{getNestedValue(profile, 'healthInfo.extraNotes')}</div>
                  </InfoItem>
                </InfoCard>
              )}
            </InfoGrid>
            
            <ActionButtons>
              <Button className="primary" onClick={handleViewSleepPlan}>
                <i className="bi bi-file-earmark-text"></i> View Sleep Plan
              </Button>
              <Button className="secondary" onClick={handleEditProfile}>
                <i className="bi bi-pencil"></i> Edit Profile
              </Button>
            </ActionButtons>
          </ProfileContent>
        </ProfileCard>
      </PageContainer>
    </div>
  );
};

export default BabyProfileView;
